import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../../services/axiosInstance";
import { reconciliationService } from "../../../services/reportService";

/**
 * SUMMARY DASHBOARD
 */
export const fetchDashboard = createAsyncThunk(
    "dashboard/fetch",
    async (arg = "", { rejectWithValue }) => {

        // terima string warehouseId atau object { warehouse_id, period }
        const isObj = arg && typeof arg === "object";
        const warehouseId = isObj ? arg.warehouse_id : arg;
        const period = isObj && arg.period ? arg.period : "daily";

        try {
            const res = await axios.get("/dashboard", {
                params: {
                    warehouse_id: warehouseId,
                    period
                }
            });
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/**
 * MONITORING RACK
 */
export const fetchRacks = createAsyncThunk(
    "dashboard/fetchRacks",
    async (warehouseId = "", { rejectWithValue }) => {
        try {
            const res = await axios.get("/daftar-racks", {
                params: {
                    warehouse_id: warehouseId
                }
            });

            return res.data;
        } catch (err) {
            return rejectWithValue(
                err.response?.data || err.message
            );
        }
    }
);

export const searchRacks = createAsyncThunk(
    "dashboard/searchRacks",
    async ({ warehouse_id = "", keyword = "" }, { rejectWithValue }) => {
        try {

            const res = await axios.get(
                "/dashboard/search-rack",
                {
                    params: {
                        warehouse_id,
                        keyword
                    }
                }
            );

            return res.data;

        } catch (err) {

            return rejectWithValue(
                err.response?.data || err.message
            );

        }
    }
);

/**
 * DETAIL RACK (POPUP)
 */
export const fetchRackDetail = createAsyncThunk(
    "dashboard/fetchDetail",
    async ({ id, code }, { rejectWithValue }) => {
        try {
            const res = await axios.get(`/dashboard-racks/${id}/${code}`);

            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchRackListMove = createAsyncThunk(
    "dashboard/fetchRackListMove",
    async (_, { rejectWithValue }) => {
        try {

            const res = await axios.get("/rack-list-move");

            return res.data;

        } catch (err) {

            return rejectWithValue(
                err.response?.data || err.message
            );

        }
    }
);


export const fetchRecommendation = createAsyncThunk(
    "dashboard/fetchRecommendation",
    async (_, { rejectWithValue }) => {
        try {
            const res = await axios.get("/dashboard-recommendation");
            return res.data;
        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

/**
 * STOCK ACCURACY (WMS x Oracle)
 * Dihitung dari data rekonsiliasi: item yang qty WMS = qty Oracle / total item.
 */
export const fetchStockAccuracy = createAsyncThunk(
    "dashboard/fetchStockAccuracy",
    async (warehouseId = "", { rejectWithValue }) => {
        try {

            const res = await reconciliationService.getReconciliationReport({
                warehouse_id: warehouseId
            });

            const rows = res.data?.data || [];
            const total = rows.length;
            const matched = rows.filter(
                (r) => String(r.status || "").toUpperCase() !== "SELISIH"
            ).length;

            return {
                total,
                matched,
                mismatched: total - matched,
                accuracy: total > 0 ? (matched / total) * 100 : null,
                last_refresh_date: res.data?.last_refresh_date || null
            };

        } catch (err) {
            return rejectWithValue(err.response?.data || err.message);
        }
    }
);

export const fetchPalletCapacity = createAsyncThunk(
    "dashboard/fetchPalletCapacity",
    async (arg, { rejectWithValue }) => {

        // terima string warehouseId atau object { warehouse_id }
        const warehouseId =
            arg && typeof arg === "object" ? arg.warehouse_id : arg;

        try {

            const res = await axios.get(
                `/report-pallet-capacity`,
                {
                    params: {
                        warehouse_id: warehouseId
                    }
                }
            );

            return res.data;

        } catch (err) {

            return rejectWithValue(
                err.response?.data || err.message
            );

        }
    }
);

const slice = createSlice({
    name: "dashboard",
    initialState: {
        summary: {},
        lastUpdated: null,
        racks: [],
        rackDetail: [],
        rackListMove: [],

        //tambahan penting
        recommendation: {
            rack_recommendation: [],
            fefo: []
        },

        palletCapacity: {
            summary: {},
            bin_type: []
        },

        racksCapacity: [],
        searchRacks: [],

        stockAccuracy: {
            total: 0,
            matched: 0,
            mismatched: 0,
            accuracy: null,
            last_refresh_date: null
        },

        loading: {
            dashboard: false,
            racks: false,
            rackDetail: false,
            searchRacks: false,
            recommendation: false,
            palletCapacity: false,
            rackListMove: false,
            stockAccuracy: false
        },

        error: null
    },

    reducers: {
        clearDetail: (state) => {
            state.rackDetail = [];
        }
    },

    extraReducers: (builder) => {
        builder

            /**
             * SUMMARY
             */
            .addCase(fetchDashboard.pending, (state) => {
                state.loading.dashboard = true;
            })
            .addCase(fetchDashboard.fulfilled, (state, action) => {
                state.loading.dashboard = false;
                state.summary = action.payload;
                state.lastUpdated =
                    action.payload?.server_time || new Date().toISOString();
            })
            .addCase(fetchDashboard.rejected, (state, action) => {
                state.loading.dashboard = false;
                state.error = action.payload;
            })

            /**
             * RACK MONITORING
             */
            .addCase(fetchRacks.pending, (state) => {
                state.loading.racks = true;
            })
            .addCase(fetchRacks.fulfilled, (state, action) => {
                state.loading.racks = false;
                state.racks = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload.data ?? [];
            })
            .addCase(fetchRacks.rejected, (state, action) => {
                state.loading.racks = false;
                state.error = action.payload;
            })

            /**
             * DETAIL
             */
            .addCase(fetchRackDetail.pending, (state) => {
                state.loading.rackDetail = true;
            })
            .addCase(fetchRackDetail.fulfilled, (state, action) => {
                //console.log("SET rackDetail", action.payload);
                state.rackDetail = action.payload;

                //console.log("STATE AFTER SET", state.rackDetail);
                state.loading.rackDetail = false;
                //state.rackDetail = action.payload.details || [];
            })
            .addCase(fetchRackDetail.rejected, (state, action) => {
                state.loading.rackDetail = false;
                state.error = action.payload;
            })

            /**
             * RECOMMENDATION
             */
            .addCase(fetchRecommendation.pending, (state) => {
                state.loading.recommendation = true;
            })
            .addCase(fetchRecommendation.fulfilled, (state, action) => {
                state.loading.recommendation = false;
                state.recommendation = action.payload;
            })
            .addCase(fetchRecommendation.rejected, (state, action) => {
                state.loading.recommendation = false;
                state.error = action.payload;
            })

            /**
             * RACK LIST MOVE
             */
            .addCase(fetchRackListMove.pending, (state) => {

                state.loading.rackListMove = true;

            })
            .addCase(fetchRackListMove.fulfilled, (state, action) => {

                state.loading.rackListMove = false;

                state.rackListMove = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload.data ?? [];

            })
            .addCase(fetchRackListMove.rejected, (state, action) => {

                state.loading.rackListMove = false;

                state.error = action.payload;

            })

            .addCase(searchRacks.pending, (state) => {

                state.loading.searchRacks = true;

            })
            .addCase(searchRacks.fulfilled, (state, action) => {

                state.loading.searchRacks = false;

                state.searchRacks = Array.isArray(action.payload)
                    ? action.payload
                    : action.payload.data ?? [];

            })
            .addCase(searchRacks.rejected, (state, action) => {

                state.loading.searchRacks = false;

                state.error = action.payload;

            })

            /**
             * STOCK ACCURACY
             */
            .addCase(fetchStockAccuracy.pending, (state) => {
                state.loading.stockAccuracy = true;
            })
            .addCase(fetchStockAccuracy.fulfilled, (state, action) => {
                state.loading.stockAccuracy = false;
                state.stockAccuracy = action.payload;
            })
            .addCase(fetchStockAccuracy.rejected, (state, action) => {
                state.loading.stockAccuracy = false;
                state.error = action.payload;
            })

            //report pallet capacity
            .addCase(fetchPalletCapacity.pending, (state) => {

                state.loading.palletCapacity = true;

            })
            .addCase(fetchPalletCapacity.fulfilled, (state, action) => {

                state.loading.palletCapacity = false;

                state.palletCapacity = action.payload;
                state.racksCapacity = action.payload.racks_capacity;

            })
            .addCase(fetchPalletCapacity.rejected, (state, action) => {

                state.loading.palletCapacity = false;

                state.error = action.payload;

            });
    }
});

export const { clearDetail } = slice.actions;
export default slice.reducer;